// Scoped theme for the project header (title / version / collaborators / view
// toggle row and the Scenes / Shots panel-tools row). Extends the app theme but
// replaces its MuiButton override, which forces every variant to a filled
// purple button -- here only Export is contained, everything else is
// outlined/text.
import { createTheme } from '@mui/material/styles';
import { MUItheme } from '../../../styles/muiStyleUpdates';

const PURPLE = '#7A64EB';
const PURPLE_DARK = '#6B55E4';

// Laptop controls are 40px; touch (iPad) controls are 48px so every target
// clears the 44px minimum.
const CONTROL_HEIGHT = 40;
const TOUCH_CONTROL_HEIGHT = 48;
const TOUCH = '@media (pointer: coarse)';

const controlSize = {
  minHeight: CONTROL_HEIGHT,
  [TOUCH]: { minHeight: TOUCH_CONTROL_HEIGHT },
};

const focusRing = {
  '&.Mui-focusVisible': {
    outline: `2px solid ${PURPLE}`,
    outlineOffset: 2,
  },
};

// eslint-disable-next-line no-unused-vars
const { MuiButton: _appButton, ...appComponents } = MUItheme.components;

export const headerTheme = createTheme({
  palette: {
    primary: { main: PURPLE, dark: PURPLE_DARK, contrastText: '#ffffff' },
    background: { default: '#ffffff', paper: '#ffffff' },
  },
  shape: { borderRadius: 6 },
  spacing: 8,
  typography: {
    h6: { fontSize: 20, fontWeight: 500, lineHeight: 1.4 },
    subtitle1: { fontSize: 16, fontWeight: 500, lineHeight: 1.5 },
    body2: { fontSize: 14 },
    button: { fontSize: 14, fontWeight: 500, textTransform: 'none' },
    overline: { fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', lineHeight: 2 },
    caption: { fontSize: 12 },
  },
  components: {
    ...appComponents,

    MuiButtonBase: {
      styleOverrides: { root: focusRing },
    },

    MuiButton: {
      defaultProps: { variant: 'text', disableElevation: true },
      styleOverrides: {
        // Padding comes from the size (medium / small); icon-only controls
        // on narrow screens use size="small" to fit the half-width row.
        root: {
          ...controlSize,
          minWidth: 0,
          whiteSpace: 'nowrap',
          textTransform: 'none',
        },
        // The Shots expand/collapse control: a text button that carries the
        // section label typography.
        textInherit: { fontSize: 16, fontWeight: 500 },
        startIcon: { marginRight: 6 },
        endIcon: { marginLeft: 6 },
      },
    },

    MuiButtonGroup: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        grouped: { minWidth: 0, ...controlSize },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          width: CONTROL_HEIGHT,
          height: CONTROL_HEIGHT,
          [TOUCH]: { width: TOUCH_CONTROL_HEIGHT, height: TOUCH_CONTROL_HEIGHT },
        },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: { root: { flexShrink: 0 } },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          width: CONTROL_HEIGHT,
          height: CONTROL_HEIGHT,
          padding: 0,
          [TOUCH]: { width: TOUCH_CONTROL_HEIGHT, height: TOUCH_CONTROL_HEIGHT },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: PURPLE,
            backgroundColor: 'rgba(122, 100, 235, 0.12)',
          },
          ...focusRing,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        ...appComponents.MuiOutlinedInput.styleOverrides,
        input: {
          paddingTop: 8.5,
          paddingBottom: 8.5,
          [TOUCH]: { paddingTop: 12.5, paddingBottom: 12.5 },
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        select: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        // Inline title rename field: matches the h6 title it replaces.
        root: { fontSize: 20, fontWeight: 500 },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          ...controlSize,
        },
        label: { fontSize: 14, whiteSpace: 'nowrap' },
      },
    },
    MuiSwitch: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: PURPLE },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: PURPLE },
        },
      },
    },

    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: { fontWeight: 500 },
        sizeSmall: { height: 22, fontSize: 12 },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.06em',
          height: 16,
          minWidth: 0,
          padding: '0 4px',
          borderRadius: 4,
          pointerEvents: 'none',
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: { width: 40, height: 40, fontSize: 14, fontWeight: 500 },
      },
      variants: [
        {
          // The "Add collaborators" control that sits at the end of the
          // avatar stack.
          props: { variant: 'dashed' },
          style: {
            borderRadius: '50%',
            border: '1px dashed rgba(0, 0, 0, 0.38)',
            backgroundColor: '#ffffff',
            color: 'rgba(0, 0, 0, 0.6)',
            marginLeft: -8,
            [TOUCH]: { width: 44, height: 44 },
            '&:hover': { backgroundColor: '#F4F1FE', borderColor: PURPLE, color: PURPLE },
          },
        },
      ],
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: { border: '2px solid #ffffff', boxSizing: 'border-box' },
      },
    },

    MuiListSubheader: {
      styleOverrides: {
        root: { fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', lineHeight: '32px' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 40,
          fontSize: 14,
          [TOUCH]: { minHeight: TOUCH_CONTROL_HEIGHT },
        },
      },
    },

    MuiTooltip: {
      ...appComponents.MuiTooltip,
      defaultProps: {
        ...appComponents.MuiTooltip.defaultProps,
        // Header controls are mostly icon-only, so keyboard users need the
        // tooltip on focus too.
        disableFocusListener: false,
      },
      styleOverrides: {
        ...appComponents.MuiTooltip.styleOverrides,
        tooltip: { ...appComponents.MuiTooltip.styleOverrides.tooltip, fontSize: 11 },
      },
    },
  },
});

export default headerTheme;
